import { useState } from 'react';

interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeMB?: number;
}

interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

export const useImageCompression = () => {
  const [isCompressing, setIsCompressing] = useState(false);

  const compressImage = async (
    file: File,
    options: CompressionOptions = {}
  ): Promise<CompressionResult> => {
    const {
      maxWidth = 1920,
      maxHeight = 1920,
      quality = 0.85,
      maxSizeMB = 2
    } = options;

    setIsCompressing(true);

    try {
      // Créer une image à partir du fichier
      const img = await createImageBitmap(file);
      
      // Calculer les nouvelles dimensions en gardant le ratio
      let { width, height } = img;
      
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // Créer un canvas pour la compression
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');

      // Dessiner l'image redimensionnée
      ctx.drawImage(img, 0, 0, width, height);

      // Déterminer le format de sortie - Privilégier WebP pour meilleur compression
      let outputFormat = 'image/webp'; // WebP par défaut
      let outputQuality = quality;
      
      // Vérifier support du navigateur pour WebP
      const supportsWebP = await checkWebPSupport();
      
      if (!supportsWebP) {
        // Fallback JPEG si WebP non supporté
        outputFormat = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      } else if (file.type === 'image/png' && file.size < 500000) {
        // Garder PNG pour les petites images avec transparence
        outputFormat = 'image/png';
      }

      // Convertir en blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Compression failed'));
          },
          outputFormat,
          outputQuality
        );
      });

      // Vérifier la taille
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      let finalBlob = blob;
      let currentQuality = outputQuality;

      // Si trop gros et format supporte la qualité, réduire progressivement
      if (blob.size > maxSizeBytes && outputFormat !== 'image/png') {
        while (finalBlob.size > maxSizeBytes && currentQuality > 0.5) {
          currentQuality -= 0.1;
          finalBlob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
              (blob) => {
                if (blob) resolve(blob);
                else reject(new Error('Compression failed'));
              },
              outputFormat,
              currentQuality
            );
          });
        }
      }

      // Créer un nouveau fichier avec le blob compressé
      // Adapter l'extension au format de sortie
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      const newExtension = outputFormat === 'image/webp' ? 'webp' 
        : outputFormat === 'image/png' ? 'png' 
        : 'jpg';
      
      const compressedFile = new File(
        [finalBlob],
        `${baseName}.${newExtension}`,
        { type: finalBlob.type }
      );

      const originalSize = file.size;
      const compressedSize = compressedFile.size;
      const compressionRatio = ((1 - compressedSize / originalSize) * 100);

      console.log(`🗜️ Compression: ${(originalSize / 1024).toFixed(0)}KB → ${(compressedSize / 1024).toFixed(0)}KB (${compressionRatio.toFixed(1)}% réduit)`);

      return {
        file: compressedFile,
        originalSize,
        compressedSize,
        compressionRatio
      };

    } finally {
      setIsCompressing(false);
    }
  };

  // Vérifier le support WebP du navigateur
  const checkWebPSupport = (): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img.width === 1);
      img.onerror = () => resolve(false);
      img.src = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
    });
  };

  return {
    compressImage,
    isCompressing
  };
};
